import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Input, Modal, Toggle, useToast } from '@w7/ui'
import { sessionApi } from '@/api/sessionApi'
import { useShortKey } from '@/app/ShortKeyContext'
import { subscribeFancyChannels } from '@/lib/socket'
import { MAX_LIMIT_OPTIONS, type RateItem, type SubMarketBundle } from './types'
import { resolveSubMarketShortKeyId } from './sessionMarketHotkeys'
import { fancySocketBelongsToMarket } from './fancySocketScope'
import { fancyResultSuccessMessage } from './fancyResultResponse'
import { submarketSuspendToast } from './submarketSuspendToast'
import {
  fancyFlagsFromUpdateResponse,
  fancyStatusOn,
  fancySuspendOn,
} from './fancyToggleState'
import {
  shouldApplyRunsOnKey,
  subMarketUpdateId,
  yesRunFromNo,
} from './scoreRuns'
import {
  formatScoreRateChip,
  isScoreRateActive,
  isScoreRunActive,
  mapRatesForScoreboard,
} from './scoreRates'
import { mergeScoreboardParentSync } from './scoreboardParentSync'
import { nextNotificationFromSocket } from './scoreboardNotificationSocket'
import { nextSuspendFromSocketFrame } from './scoreboardSuspendSocket'

type TextState = {
  runner1: string | number
  runner2: string | number
  ratesNo: string
  runNo: number | null
  runButtonValue: string | number
  rateBack?: number
  rateLay?: number
  rateCategoryId?: string
  rateCategoryName?: string
}

export function ScoreDashboard({
  subMarketData: initial,
  onRefresh,
}: {
  subMarketData: SubMarketBundle
  onRefresh: () => void
}) {
  const toast = useToast()
  const { subMarketkey, setSubMarketkey } = useShortKey()
  const [bundle, setBundle] = useState(initial)
  const details = bundle.subMarketDetails

  const [text, setText] = useState<TextState>({
    runner1: '',
    runner2: '',
    ratesNo: '',
    runNo: null,
    runButtonValue: '',
  })
  const [runs, setRuns] = useState<{ runNo: number }[]>([])
  const [rates, setRates] = useState<RateItem[]>([])
  const [rateDiff, setRateDiff] = useState<number | string>('')
  const [rateCategoryArray, setRateCategoryArray] = useState<{ _id: string; name: string }[]>([])
  const [resultOpen, setResultOpen] = useState(false)
  const [voidOpen, setVoidOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [resultHorse, setResultHorse] = useState('')
  const [newSessionName, setNewSessionName] = useState('')
  const [disable, setDisable] = useState(false)
  /** Skip blur re-apply right after Enter (Lab: Enter applies No→Yes once). */
  const runsAppliedByEnterRef = useRef(false)
  const shortKeyId = resolveSubMarketShortKeyId(details)
  const shortKeysOn = subMarketkey === shortKeyId

  // Sync catalog from parent fetch, but keep live toggle/line fields for the
  // same market — blind replace races Rate clicks and blinks Suspend.
  useEffect(() => {
    setBundle((prev) =>
      mergeScoreboardParentSync(prev, {
        subMarketShow: initial.subMarketShow,
        subMarketDetails: initial.subMarketDetails,
        rateList: initial.rateList,
        runList: initial.runList,
      }),
    )
  }, [initial.subMarketShow, initial.subMarketDetails, initial.rateList, initial.runList])

  useEffect(() => {
    const list = (bundle.runList ?? []).map((r) => ({ runNo: r.runs }))
    setRuns(list)
    const runBack = details.horseBack
    const runLay = details.horseLay
    const horserateBack = details.rateBack
    const horserateLay = details.rateLay
    setText((t) => {
      const next = { ...t }
      if (runBack != null || runLay != null) {
        next.runner1 = runLay ?? ''
        next.runner2 = runBack ?? ''
        if (runBack != null && runLay != null) setRateDiff(Number(runBack) - Number(runLay))
      }
      if (horserateBack != null && horserateLay != null) {
        next.ratesNo = `${horserateLay}/${horserateBack}`
      }
      return next
    })
  }, [bundle.runList, details.horseBack, details.horseLay, details.rateBack, details.rateLay])

  useEffect(() => {
    if (!details.isSky && !details.isParker) {
      void sessionApi
        .getAllRateCategory()
        .then((res) => {
          setRateCategoryArray(Array.isArray(res.data) ? (res.data as { _id: string; name: string }[]) : [])
        })
        .catch(() => setRateCategoryArray([]))
    }
    if (details.rateCategoryId && details.rateCategoryName) {
      void loadFilterRates(
        String(details.rateCategoryId),
        typeof details.rateCategoryName === 'string'
          ? details.rateCategoryName
          : details.rateCategoryName.name,
      )
    } else if (bundle.rateList?.length) {
      ratesReflection(bundle.rateList)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const marketId = details.betfairId
    if (!marketId) return
    const handler = (data: Record<string, unknown>) => {
      if (!data) return
      // Shared statusSocket_ / Match Odds frames must not overwrite this fancy.
      if (!fancySocketBelongsToMarket(data, marketId)) return
      setBundle((b) => {
        const cur = b.subMarketDetails
        const nextRateBack =
          data.rateBack != null ? (data.rateBack as number) : cur.rateBack
        const nextRateLay =
          data.rateLay != null ? (data.rateLay as number) : cur.rateLay
        // Suspend is toggle-only — never take isSuspended from socket (full-row
        // Runs/Rate blasts match the line so the old lineMoved guard failed).
        const nextSuspended = nextSuspendFromSocketFrame(cur.isSuspended)
        return {
          ...b,
          subMarketDetails: {
            ...cur,
            fancyCommission:
              data.fancyCommission != null
                ? (data.fancyCommission as boolean)
                : cur.fancyCommission,
            isSuspended: nextSuspended,
            ballChalu:
              data.ballChalu != null ? (data.ballChalu as boolean) : cur.ballChalu,
            isMarketSuspended:
              data.isMarketSuspended != null
                ? (data.isMarketSuspended as boolean)
                : (cur as { isMarketSuspended?: boolean }).isMarketSuspended,
            minBet: data.minBet != null ? (data.minBet as number) : cur.minBet,
            maxLimitsType:
              data.maxLimitsType != null
                ? (data.maxLimitsType as string)
                : cur.maxLimitsType,
            status:
              data.status === 'Enable'
                ? true
                : data.status === 'Disable'
                  ? false
                  : data.status != null
                    ? (data.status as boolean)
                    : cur.status,
            horseBack:
              data.horseBack != null ? (data.horseBack as number) : cur.horseBack,
            horseLay: data.horseLay != null ? (data.horseLay as number) : cur.horseLay,
            rateBack: nextRateBack,
            rateLay: nextRateLay,
            runButtonValue:
              data.runButtonValue != null
                ? Number(data.runButtonValue)
                : cur.runButtonValue,
            notification: nextNotificationFromSocket(cur.notification, data),
          },
        }
      })
    }
    // Lab sendSingleSocket → betfairId + market_{id}; updateStatus also → statusSocket_{markets}.
    // Also join Match Odds channels so bulk sendNewSocket (array) reaches this screen.
    const channels = [marketId, `market_${marketId}`]
    const matchOdd = String(details.matchOddBetfairId || '').trim()
    if (matchOdd) channels.push(matchOdd, `market_${matchOdd}`)
    if (details.markets) channels.push(`statusSocket_${details.markets}`, `abandonSocket_${details.markets}`, `resultDeclared_${details.markets}`)
    return subscribeFancyChannels(channels, handler, marketId)
  }, [details.betfairId, details.markets, details.matchOddBetfairId])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!shortKeysOn) return
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      if (e.key === 'c' || e.key === 'C') {
        e.preventDefault()
        void keyCommission()
      } else if (e.key === 'e' || e.key === 'E') {
        e.preventDefault()
        void keyBallChalu()
      } else if (e.key === 's' || e.key === 'S') {
        e.preventDefault()
        void keySuspended()
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault()
        tabLeftRight(e.key === 'ArrowLeft' ? 'left' : 'right')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortKeysOn, details, text, rates])

  const ratesReflection = (rateList: RateItem[]) => {
    // Lab ResultScoreBoard / Score chips: always lay/back (e.g. 100/100), not char.
    setRates(mapRatesForScoreboard(rateList ?? []))
  }

  const loadFilterRates = async (id: string, name: string) => {
    try {
      const resp = await sessionApi.getFilterRates({ _id: id, name })
      const data = (resp.data as { data?: RateItem[] })?.data ?? []
      // Empty category → keep full catalog so the odds chip grid does not vanish.
      const nextRates = data.length ? data : (bundle.rateList ?? [])
      setBundle((b) => ({
        ...b,
        rateList: nextRates,
        subMarketDetails: {
          ...b.subMarketDetails,
          rateCategoryId: id,
          rateCategoryName: { _id: id, name },
        },
      }))
      ratesReflection(nextRates)
    } catch {
      ratesReflection(bundle.rateList ?? [])
    }
  }

  const patchDetails = (patch: Partial<typeof details>) => {
    setBundle((b) => ({ ...b, subMarketDetails: { ...b.subMarketDetails, ...patch } }))
  }

  const runsFunc = async (run: number) => {
    const hasNo = text.runner1 !== '' && text.runner1 != null
    const horseLay = hasNo ? Number(text.runner1) : undefined
    const horseBack = hasNo ? Number(run) + Number(text.runner1) : run
    const next = { ...text, runButtonValue: run, runner2: horseBack }
    setText(next)
    // Optimistic horses so stale suspend:true + old runs sockets cannot win.
    patchDetails({
      runButtonValue: run,
      horseBack,
      ...(horseLay != null && Number.isFinite(horseLay) ? { horseLay } : {}),
    })
    try {
      // Runs-only body (not full TextState) — avoid accidental rate/suspend fields.
      const res = await sessionApi.subMarketUpdate(details.marketId, {
        runButtonValue: run,
        horseBack,
        ...(horseLay != null && Number.isFinite(horseLay) ? { horseLay, runner1: horseLay } : {}),
        runner2: horseBack,
      })
      const data = res.data as {
        horseBack?: number
        horseLay?: number
        runButtonValue?: number
      }
      // Suspend is toggle-only — never apply isSuspended from Runs responses.
      patchDetails({
        horseBack: data.horseBack ?? horseBack,
        horseLay: data.horseLay ?? horseLay,
        runButtonValue: data.runButtonValue ?? run,
      })
    } catch (err: unknown) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Update failed',
      )
    }
  }

  const rateFunc = async (rate: RateItem | undefined) => {
    if (!rate) return
    const cat =
      typeof details.rateCategoryName === 'object' && details.rateCategoryName
        ? details.rateCategoryName
        : { _id: String(details.rateCategoryId ?? ''), name: String(details.rateCategoryName ?? '') }
    const next: TextState = {
      ...text,
      ratesNo: formatScoreRateChip(rate) || rate.rateNo,
      rateBack: rate.rateBack,
      rateLay: rate.rateLay,
      runButtonValue: details.runButtonValue ?? text.runButtonValue,
      rateCategoryId: cat._id,
      rateCategoryName: cat.name,
    }
    setText(next)
    // Optimistic line only — do not send runners (that was clearing/re-writing
    // horse + suspend on every Rate click and racing the Suspend toggle).
    patchDetails({ rateBack: rate.rateBack, rateLay: rate.rateLay })
    try {
      const res = await sessionApi.subMarketUpdate(details.marketId, {
        rateBack: rate.rateBack,
        rateLay: rate.rateLay,
      })
      const data = res.data as {
        rateBack?: number
        rateLay?: number
      }
      // Suspend is toggle-only — never apply isSuspended from Rate responses.
      patchDetails({
        rateBack: data.rateBack ?? rate.rateBack,
        rateLay: data.rateLay ?? rate.rateLay,
      })
    } catch (err: unknown) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Update failed',
      )
    }
  }

  const tabLeftRight = (dir: 'left' | 'right') => {
    const idx = rates.findIndex((r) => r.rateNo === text.ratesNo)
    if (idx < 0) return
    const next = dir === 'left' ? rates[idx - 1] : rates[idx + 1]
    void rateFunc(next)
  }

  const onChangeRuns = async (run: string) => {
    setDisable(!run)
    const runner2 = yesRunFromNo(run, {
      runNo: text.runNo,
      rateDiff,
      runButtonValue: details.runButtonValue ?? text.runButtonValue,
    })
    const next = {
      ...text,
      runner1: run,
      runner2,
      runButtonValue: details.runButtonValue ?? text.runButtonValue,
    }
    setText(next)
    const marketId = subMarketUpdateId(details)
    if (!marketId) {
      toast.error('Market id missing')
      return
    }
    try {
      const res = await sessionApi.subMarketUpdate(marketId, {
        runner1: next.runner1,
        runner2: next.runner2,
        horseLay: next.runner1,
        horseBack: next.runner2,
        runButtonValue: next.runButtonValue,
      })
      const data = res.data as {
        horseBack?: number
        horseLay?: number
        runButtonValue?: number
      }
      // Suspend is toggle-only — do not apply fancyFlags suspend from Enter-on-No.
      patchDetails({
        horseBack: data.horseBack ?? Number(next.runner2),
        horseLay: data.horseLay ?? Number(next.runner1),
        runButtonValue: data.runButtonValue ?? Number(next.runButtonValue),
      })
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Update failed')
    }
  }

  const changeCommission = async (nextVal: boolean) => {
    const prev = details.fancyCommission
    patchDetails({ fancyCommission: nextVal })
    try {
      const res = await sessionApi.updateCommission(details.marketId, { fancyCommission: nextVal })
      const data = res.data as { fancyCommission?: boolean }
      patchDetails({ fancyCommission: data.fancyCommission ?? nextVal })
      toast.success(`Submarket Commission Updated for ${details.name}`)
    } catch (err: unknown) {
      patchDetails({ fancyCommission: prev })
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed')
    }
  }

  const keyCommission = async () => {
    if (!shortKeysOn) return
    await changeCommission(!details.fancyCommission)
  }

  const changeStatus = async (enabled: boolean) => {
    const prev = details.status
    const status = enabled ? 'Enable' : 'Disable'
    patchDetails({ status: enabled })
    try {
      await sessionApi.subMarketStatus(details.marketId, { status })
      toast.success(`Submarket Status Updated for ${details.name}`)
    } catch (err: unknown) {
      patchDetails({ status: prev })
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed')
    }
  }

  const changeBallChalu = async (nextVal: boolean) => {
    const prev = details.ballChalu
    patchDetails({ ballChalu: nextVal })
    try {
      const res = await sessionApi.updateBallChalu(details.marketId, { ballChalu: nextVal })
      const data = res.data as { ballChalu?: boolean }
      patchDetails({ ballChalu: data.ballChalu ?? nextVal })
      toast.success(`Submarket  Ball Running Updated for ${details.name}`)
    } catch (err: unknown) {
      patchDetails({ ballChalu: prev })
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed')
    }
  }

  const keyBallChalu = async () => {
    if (!shortKeysOn) return
    await changeBallChalu(!details.ballChalu)
  }

  const subMarketSuspended = async (nextVal: boolean) => {
    const prevSuspended = details.isSuspended
    const prevMarket = (details as { isMarketSuspended?: boolean }).isMarketSuspended
    // Per-fancy Suspend API clears/sets isSuspended; keep market flag in sync for UI.
    patchDetails({ isSuspended: nextVal, isMarketSuspended: nextVal ? prevMarket : false })
    try {
      const res = await sessionApi.subMarketUpdate(details.marketId, { isSubMarketSuspended: nextVal })
      const data = res.data as { isSuspended?: boolean; isMarketSuspended?: boolean }
      // Force toggle value — do not let a stale response flip Suspend back.
      patchDetails({
        ...fancyFlagsFromUpdateResponse(data),
        isSuspended: typeof data.isSuspended === 'boolean' ? data.isSuspended : nextVal,
      })
      toast.success(submarketSuspendToast(details.name, nextVal))
    } catch (err: unknown) {
      patchDetails({ isSuspended: prevSuspended, isMarketSuspended: prevMarket })
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed')
    }
  }

  const keySuspended = async () => {
    if (!shortKeysOn) return
    await subMarketSuspended(!fancySuspendOn(details))
  }

  const showResult = async () => {
    if (resultHorse === '' || resultHorse == null) {
      toast.error('Result required')
      return
    }
    try {
      const res = await sessionApi.resultDeclared({
        horse: resultHorse,
        subMarketId: details.marketId,
        marketId: details.marketId,
        market: details.markets,
      })
      toast.success(fancyResultSuccessMessage(res.data))
      setResultHorse('')
      setResultOpen(false)
      onRefresh()
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { message?: string } } }
      if (e.response?.status === 500) toast.error('Result Already Given / Enter the valid result')
      else toast.error(e.response?.data?.message ?? 'SubMarket not found')
    }
  }

  const abandon = async () => {
    try {
      await sessionApi.subMarketUpdateForVoid(details.marketId, { isAbandon: true })
      toast.success('Abandoned Succesfully')
      setVoidOpen(false)
      onRefresh()
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed')
    }
  }

  const editSessionName = async () => {
    if (!newSessionName.trim()) {
      toast.error('Session Name required')
      return
    }
    try {
      const res = await sessionApi.editSessionName({ name: newSessionName, marketId: details.marketId })
      const data = res.data as { action?: string }
      if (data?.action === 'Name Updated') {
        toast.success('Session Name Updated Succesfully')
        setEditOpen(false)
        onRefresh()
      } else {
        toast.error('Please try again')
        setEditOpen(false)
      }
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Please try again')
      setEditOpen(false)
    }
  }

  const catValue = useMemo(() => {
    const c = details.rateCategoryName
    if (c && typeof c === 'object') return c._id
    return String(details.rateCategoryId ?? '')
  }, [details.rateCategoryId, details.rateCategoryName])

  const statusOn = fancyStatusOn(details.status)
  const suspendOn = fancySuspendOn(details)

  return (
    <div className="sm-submarket" id={details.name}>
      <div className="sm-row" style={{ justifyContent: 'space-between' }}>
        <div className="sm-row">
          <strong>
            {details.name}
            {details.isSky ? ' - Sky' : ''}
            {details.isParker ? ' - Parker' : ''}
          </strong>
          <Button
            size="sm"
            onClick={() => {
              setNewSessionName(details.name)
              setEditOpen(true)
            }}
          >
            Edit
          </Button>
        </div>
        <div className="sm-row">
          {!details.isSky && !details.isParker ? (
            <span className="w7-dim">
              Runs {String(text.runner1)}/{String(text.runner2)} · Rate {text.ratesNo}
            </span>
          ) : null}
          <Button
            variant="primary"
            size="sm"
            disabled={(!details.isAbandon && (text.runner1 == null || text.runner1 === '')) || (disable && !text.runner1)}
            onClick={() => setResultOpen(true)}
          >
            Result
          </Button>
          <label className="sm-toggle">
            Short Keys
            <Toggle
              label="Short Keys"
              checked={shortKeysOn}
              onChange={(on) => {
                setSubMarketkey(on ? shortKeyId : '')
                if (on) toast.success('Short Keys Enabled')
              }}
            />
          </label>
          <Button
            size="sm"
            onClick={() => setVoidOpen(true)}
            title="Void"
          >
            ×
          </Button>
        </div>
      </div>

      <div className="sm-row" style={{ alignItems: 'flex-start', marginTop: 10 }}>
        <div style={{ flex: 1, minWidth: 280 }}>
          {!details.isAbandon ? (
            <div className="sm-grid-controls">
              <label className="sm-toggle" title="c">
                Commission
                <Toggle
                  label="Commission"
                  checked={!!details.fancyCommission}
                  onChange={(v) => void changeCommission(v)}
                />
              </label>
              <label className="sm-toggle">
                Status
                <Toggle label="Status" checked={!!statusOn} onChange={(v) => void changeStatus(v)} />
              </label>
              {!details.isSky && !details.isParker ? (
                <>
                  <label className="sm-toggle" title="e">
                    Ball Running
                    <Toggle
                      label="Ball Running"
                      checked={!!details.ballChalu}
                      onChange={(v) => void changeBallChalu(v)}
                    />
                  </label>
                  <label className="sm-toggle" title="s">
                    Suspend
                    <Toggle
                      label="Suspend"
                      checked={suspendOn}
                      onChange={(v) => void subMarketSuspended(v)}
                    />
                  </label>
                </>
              ) : null}
              <label>
                Order
                <Input
                  type="number"
                  value={String(details.order ?? '')}
                  onChange={(e) => patchDetails({ order: e.target.value })}
                  onBlur={() => void sessionApi.changeOrder(details.marketId, details.order)}
                />
              </label>
              <label>
                Bet Delay
                <Input
                  type="number"
                  value={String(details.betDelay ?? '')}
                  onChange={(e) => patchDetails({ betDelay: e.target.value })}
                  onBlur={() => void sessionApi.changeBetDelay(details.marketId, details.betDelay)}
                />
              </label>
              <label>
                Min Bet
                <Input
                  type="number"
                  value={String(details.minBet ?? '')}
                  onChange={(e) => patchDetails({ minBet: e.target.value })}
                  onBlur={() => void sessionApi.changeMinBet(details.marketId, details.minBet)}
                />
              </label>
              <label>
                Max limits
                <select
                  className="w7-input"
                  value={String(details.maxLimitsType ?? '')}
                  onChange={(e) => {
                    const maxLimitsType = e.target.value
                    patchDetails({ maxLimitsType })
                    void sessionApi
                      .changeMaxLimitsType(details._id, maxLimitsType, details.markets, details.marketId)
                      .then(() => toast.success('Changed Limits Type'))
                      .catch(() => toast.error('No Competition Found'))
                  }}
                >
                  <option value="">Select</option>
                  {MAX_LIMIT_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : null}

          {!details.isSky && !details.isParker ? (
            <div className="sm-row sm-row-save" style={{ marginTop: 12 }}>
              <label>
                Max Limit To Suspend
                <Input
                  type="number"
                  value={String(details.suspendMaxLimit ?? '')}
                  onChange={(e) => patchDetails({ suspendMaxLimit: e.target.value })}
                />
              </label>
              <Button
                size="sm"
                variant="primary"
                onClick={() =>
                  void sessionApi.changeSuspendMaxLimit(details.marketId, details.suspendMaxLimit).then(() =>
                    toast.success(`Max Suspend Limit Updated Successfully for ${details.name} Submarket`),
                  )
                }
              >
                Save
              </Button>
            </div>
          ) : null}

          <div className="sm-row sm-row-save" style={{ marginTop: 12 }}>
            <label>
              Notification Text
              <Input
                value={String(details.notification ?? '')}
                onChange={(e) => patchDetails({ notification: e.target.value })}
              />
            </label>
            <Button
              size="sm"
              variant="primary"
              onClick={() =>
                void sessionApi.changeMarketNotification(details.marketId, details.notification).then(() =>
                  toast.success(`Notification Updated Successfully for ${details.name} Submarket`),
                )
              }
            >
              Save
            </Button>
          </div>
        </div>

        {!details.isSky && !details.isParker ? (
          <div style={{ flex: 1, minWidth: 0, maxWidth: '100%' }}>
            <div className="sm-row">
              <span>Runs</span>
              <Input
                type="number"
                value={String(text.runner1)}
                onChange={(e) => setText((t) => ({ ...t, runner1: e.target.value }))}
                onKeyDown={(e) => {
                  // Lab: Enter on No → auto-fill Yes and persist horseLay/horseBack.
                  if (!shouldApplyRunsOnKey(e.key)) return
                  e.preventDefault()
                  runsAppliedByEnterRef.current = true
                  void onChangeRuns(e.currentTarget.value)
                }}
                onBlur={(e) => {
                  if (runsAppliedByEnterRef.current) {
                    runsAppliedByEnterRef.current = false
                    return
                  }
                  void onChangeRuns(e.target.value)
                }}
                style={{ width: 80 }}
                aria-label="Runs No"
              />
              <Input
                value={String(text.runner2)}
                readOnly
                style={{ width: 80 }}
                aria-label="Runs Yes"
              />
            </div>
            <div className="sm-row sm-result-preset-row" style={{ marginTop: 8 }}>
              <div className="sm-result-wrap" role="list" aria-label="Runs options">
                {runs.map((run) => (
                  <button
                    key={run.runNo}
                    type="button"
                    className={`sm-run-btn${
                      isScoreRunActive({
                        runNo: run.runNo,
                        runButtonValue: details.runButtonValue,
                        runner1: text.runner1,
                        runner2: text.runner2,
                      })
                        ? ' active'
                        : ''
                    }`}
                    onClick={() => void runsFunc(run.runNo)}
                  >
                    {run.runNo}
                  </button>
                ))}
              </div>
            </div>
            <div className="sm-row" style={{ marginTop: 10 }}>
              <span>Rate Category</span>
              <select
                className="w7-input"
                value={catValue}
                onChange={(e) => {
                  const item = rateCategoryArray.find((x) => x._id === e.target.value)
                  if (item) void loadFilterRates(item._id, item.name)
                }}
              >
                <option value="">Rate category</option>
                {rateCategoryArray.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <Input value={text.ratesNo} readOnly style={{ width: 100 }} />
            </div>
            <div className="sm-row sm-chip-row" style={{ marginTop: 8 }}>
              <div className="sm-chip-scroll" role="list" aria-label="Rate options">
                {rates.map((rate) => {
                  const label = formatScoreRateChip(rate) || rate.rateNo
                  return (
                    <button
                      key={label || `${rate.rateLay}/${rate.rateBack}`}
                      type="button"
                      className={`sm-run-btn${isScoreRateActive(text.ratesNo, rate) ? ' active' : ''}`}
                      onClick={() => void rateFunc(rate)}
                    >
                      {label}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <Modal
        open={resultOpen}
        onClose={() => setResultOpen(false)}
        title="Add Result"
        footer={
          <div className="sm-row" style={{ justifyContent: 'flex-end' }}>
            <Button onClick={() => setResultOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={() => void showResult()}>
              OK
            </Button>
          </div>
        }
      >
        <div>{details.name}</div>
        <Input
          type="number"
          value={resultHorse}
          onChange={(e) => setResultHorse(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void showResult()
          }}
        />
      </Modal>

      <Modal
        open={voidOpen}
        onClose={() => setVoidOpen(false)}
        title="Abandon"
        footer={
          <div className="sm-row" style={{ justifyContent: 'flex-end' }}>
            <Button onClick={() => setVoidOpen(false)}>No</Button>
            <Button variant="primary" onClick={() => void abandon()}>
              Yes
            </Button>
          </div>
        }
      >
        <p>Do you want to abandon the submarket &quot;{details.name}&quot;?</p>
      </Modal>

      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit Session Name"
        footer={
          <div className="sm-row" style={{ justifyContent: 'flex-end' }}>
            <Button onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={() => void editSessionName()}>
              OK
            </Button>
          </div>
        }
      >
        <label>
          Session Name:
          <Input value={newSessionName} onChange={(e) => setNewSessionName(e.target.value)} maxLength={100} />
        </label>
      </Modal>
    </div>
  )
}
